import React, { useState } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { content } from '../data/content';
import { Logo } from './ui/Logo';
import { Modal } from './ui/Modal';
import { ThemeToggle, LanguageToggle } from './ui/Toggle';

const COL = ({ label, children }) => (
  <div>
    <p className="eyebrow mb-4">{label}</p>
    <ul className="space-y-3">{children}</ul>
  </div>
);

const FLink = ({ href = '#', onClick, children }) => (
  <li>
    <a
      href={href}
      onClick={onClick}
      className="text-muted hover:text-app transition-colors link-underline"
    >
      {children}
    </a>
  </li>
);

export const Footer = () => {
  const { language } = useLanguage();
  const t = content[language];
  const [open, setOpen] = useState(null); // 'data' | 'imprint' | null

  const goTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const year = new Date().getFullYear();

  return (
    <footer className="relative pt-14 sm:pt-20 pb-8 sm:pb-10 bg-elev border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="absolute inset-0 bg-dotgrid opacity-40 pointer-events-none" />
      <div className="container relative">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo className="h-16 md:h-20" />
            <p className="mt-4 text-muted max-w-sm leading-relaxed">{t.footer.tagline}</p>
            <div className="mt-6 flex items-center gap-2">
              <LanguageToggle />
              <ThemeToggle />
            </div>
          </div>

          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            <COL label={t.footer.columns.services}>
              <FLink href="#services" onClick={goTo('services')}>{t.footer.links.services}</FLink>
              <FLink href="#process"  onClick={goTo('process')}>{t.footer.links.process}</FLink>
              <FLink href="#work"     onClick={goTo('work')}>{t.footer.links.work}</FLink>
              <FLink href="#pricing"  onClick={goTo('pricing')}>{t.footer.links.pricing}</FLink>
            </COL>
            <COL label={t.footer.columns.company}>
              <FLink href="#faq"     onClick={goTo('faq')}>{t.footer.links.faq}</FLink>
              <FLink href="#contact" onClick={goTo('contact')}>{t.footer.links.contact}</FLink>
              <FLink href="mailto:info@emfa-it.de">info@emfa-it.de</FLink>
              <FLink href="tel:+491715592218">+49 171 5592218</FLink>
            </COL>
            <COL label={t.footer.columns.legal}>
              <FLink onClick={(e) => { e.preventDefault(); setOpen('data'); }} href="#data">
                {t.footer.dataProtection}
              </FLink>
              <FLink onClick={(e) => { e.preventDefault(); setOpen('imprint'); }} href="#imprint">
                {t.footer.imprint}
              </FLink>
            </COL>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-3"
             style={{ borderColor: 'var(--border)' }}>
          <p className="text-sm text-subtle">© {year} EMFA IT Consulting — {t.footer.rights}</p>
          <p className="text-mono text-xs uppercase tracking-[0.2em] text-subtle">{t.footer.built}</p>
        </div>
      </div>

      <Modal open={open === 'data'} onClose={() => setOpen(null)} title={t.footer.dataProtection}>
        {t.legal.dataProtection}
      </Modal>
      <Modal open={open === 'imprint'} onClose={() => setOpen(null)} title={t.footer.imprint}>
        {t.legal.imprint}
      </Modal>
    </footer>
  );
};

export default Footer;
